import { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, ChevronDown, ChevronUp, Shield, Heart, Award, Users, Phone, Mail, Clock, Search, ArrowRight } from "lucide-react";
import '../index.css';

export default function FAQs() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Categories for filtering FAQs
  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'general', name: 'General' },
    { id: 'health', name: 'Health Insurance' },
    { id: 'life', name: 'Life Insurance' },
    { id: 'senior', name: 'Senior Plans' },
    { id: 'coverage', name: 'Coverage Details' },
    { id: 'payments', name: 'Payments & Claims' }
  ];
  
  // List of FAQs
  const faqs = [
    {
      id: 1,
      question: "What makes Optimal Actuarion different from other insurance providers?",
      answer: "Optimal Actuarion stands out through our commitment to inclusivity and affordability. We specifically focus on providing comprehensive coverage for senior citizens and underserved communities. Our client-first approach ensures transparent policies with no hidden fees or exclusions, and we work directly with insurance providers to negotiate the best rates for our clients.",
      category: "general"
    },
    {
      id: 2,
      question: "Do you offer specialized plans for senior citizens?",
      answer: "Yes, we specialize in insurance solutions for senior citizens. Our senior plans are designed to address specific health concerns and needs of older adults, with affordable premiums and comprehensive coverage options including prescription medications, specialist visits, and preventive care. We also offer assistance with Medicare supplemental coverage.",
      category: "senior"
    },
    {
      id: 3,
      question: "How quickly can I get coverage?",
      answer: "In most cases, we can process your application and get you coverage within 24-48 hours after approval. For some specialized policies, it may take 3-5 business days. Our team works diligently to expedite the process while ensuring thorough evaluation of your needs.",
      category: "general"
    },
    {
      id: 4,
      question: "What health insurance options do you offer?",
      answer: "We offer a wide range of health insurance options including comprehensive medical coverage, supplemental plans, Medicare supplements, short-term medical insurance, critical illness coverage, and specialized plans for chronic conditions. Each plan can be customized based on your specific healthcare needs and budget.",
      category: "health"
    },
    {
      id: 5,
      question: "How do I file a claim?",
      answer: "Filing a claim with Optimal Actuarion is simple. You can submit claims through our online portal, by email, or by calling our dedicated claims department at 972-891-8635. Our representatives will guide you through the process and required documentation. Most claims are processed within 5-7 business days.",
      category: "payments"
    },
    {
      id: 6,
      question: "What life insurance coverage amounts are available?",
      answer: "Our life insurance coverage amounts range from $10,000 to $2 million, depending on the policy type and your eligibility. We offer term life, whole life, and universal life insurance options with various benefit levels to suit your financial protection goals and budget.",
      category: "life"
    },
    {
      id: 7,
      question: "Do you require medical exams for insurance coverage?",
      answer: "This depends on the type of policy and coverage amount. Many of our policies offer simplified underwriting with no medical exam required. For larger coverage amounts or specific policy types, a medical examination may be necessary. We also offer guaranteed issue policies for those who may not qualify for traditional coverage.",
      category: "coverage"
    },
    {
      id: 8,
      question: "Can I customize my insurance policy?",
      answer: "Absolutely! We believe in personalized coverage that meets your specific needs. Our insurance specialists work with you to create a customized policy by adding riders, adjusting coverage limits, and combining different policy types to ensure you have exactly the protection you need.",
      category: "coverage"
    },
    {
      id: 9,
      question: "What payment methods do you accept?",
      answer: "We accept various payment methods including credit/debit cards, electronic funds transfer (EFT), bank drafts, personal checks, and online payment platforms. We also offer flexible payment schedules – monthly, quarterly, semi-annually, or annually – often with discounts for annual payments.",
      category: "payments"
    },
    {
      id: 10,
      question: "How can I get a quote?",
      answer: "Getting a quote is easy and free! You can request a quote online through our website by filling out our simple form, call us directly at 972-891-8635, or visit our office in Irving, TX. Our insurance specialists will gather some basic information and provide you with customized quotes that fit your needs and budget.",
      category: "general"
    },
    {
      id: 11,
      question: "Are there discounts available for multiple policies?",
      answer: "Yes, we offer multi-policy discounts when you bundle different types of insurance with us. For example, combining health and life insurance can save you up to 15% on premiums. We also offer family coverage discounts, loyalty discounts for long-term clients, and special rates for various professional groups.",
      category: "payments"
    },
    {
      id: 12,
      question: "What is the waiting period for pre-existing conditions?",
      answer: "Waiting periods for pre-existing conditions vary by policy type and condition. Typically, our health insurance policies have waiting periods ranging from 6-12 months for pre-existing conditions. However, some of our specialized senior plans offer shorter waiting periods or coverage for certain pre-existing conditions from day one.",
      category: "health"
    },
    {
      id: 13,
      question: "Do you offer insurance for people with chronic conditions?",
      answer: "Yes, we specialize in finding coverage solutions for individuals with chronic conditions. We work with insurance providers that offer inclusive policies designed specifically for various chronic health conditions. Our team will help match you with the best options that provide the coverage you need at affordable rates.",
      category: "health"
    },
    {
      id: 14,
      question: "What happens if I need to cancel my policy?",
      answer: "If you need to cancel your policy, simply contact our customer service team. Most of our policies include a free look period (typically 10-30 days) during which you can cancel for a full refund. After this period, cancellation terms vary by policy type. We prorate premiums for the unused policy period and process refunds within 7-10 business days.",
      category: "general"
    },
    {
      id: 15,
      question: "Do you offer coverage for prescription medications?",
      answer: "Yes, many of our health insurance plans include prescription drug coverage. The extent of coverage depends on the specific plan you choose. We also offer standalone prescription drug plans and Medicare Part D plans for seniors. Our specialists can help you find the best option based on your current medications and needs.",
      category: "health"
    }
  ];
  
  // Filter FAQs based on category and search query
  const filteredFAQs = faqs.filter(faq => 
    (activeCategory === 'all' || faq.category === activeCategory) &&
    (faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
     faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // State for expanded FAQ items
  const [expandedFAQs, setExpandedFAQs] = useState({});
  
  // Toggle FAQ expansion
  const toggleFAQ = (id) => {
    setExpandedFAQs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Reset expanded FAQs when category changes
  useEffect(() => {
    setExpandedFAQs({});
  }, [activeCategory]);
  
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        {/* Announcement bar */}
        <div className="bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white text-center py-2 text-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-semibold mr-2">Limited Time:</span> Get up to 30% off your first year of coverage. 
            <a href="/promotion" className="font-bold underline ml-1 hover:text-blue-100 transition-colors">Learn more</a>
          </div>
        </div>
        
        <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="mr-2 text-[#0077B6]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-2xl font-extrabold bg-gradient-to-r from-[#002D62] to-[#0077B6] bg-clip-text text-transparent">Optimal Actuarion</span>
              </div>
              <nav className="hidden md:ml-10 md:flex md:space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Home</a>
                <a href="/#/about" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">About Us</a>
                <a href="/#/services" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Services</a>
                <a href="/#/quote" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Get a Quote</a>
                <a href="/#/faqs" className="text-[#002D62] font-bold border-b-2 border-[#0077B6] px-1 pt-2 pb-1 transition-colors">FAQs</a>
                <a href="/#/testimonials" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Testimonials</a>
                <a href="/#/contact" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Contact</a>
              </nav>
            </div>
            <div className="flex items-center">
              <div className="hidden md:flex md:items-center md:space-x-4">
                <a href="/login" className="text-[#002D62] font-medium hover:text-[#0077B6] mr-4 transition-colors">Login</a>
                <a href="/quote" className="bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-6 py-3 rounded-md font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200">
                  Get a Free Quote
                </a>
              </div>
              <div className="flex md:hidden">
                <button
                  type="button"
                  className="text-gray-700 hover:text-[#002D62] transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg animate-fadeIn">
            <div className="pt-2 pb-4 space-y-1 px-4">
            <a href="/" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Home</a>
              <a href="/#/about" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">About Us</a>
              <a href="/#/services" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Services</a>
              <a href="/#/quote" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Get a Quote</a>
              <a href="/#/faqs" className="block py-2 text-[#002D62] font-bold border-l-4 border-[#0077B6] pl-3">FAQs</a>
              <a href="/#/testimonials" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Testimonials</a>
              <a href="/#/contact" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Contact</a>
             <div className="pt-4 pb-2 space-y-2">
                <a href="/login" className="block text-center text-[#002D62] font-medium hover:text-[#0077B6] py-2 transition-colors">
                  Login
                </a>
                <a href="/quote" className="w-full flex items-center justify-center bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-4 py-3 rounded-md font-bold shadow-md transition-all hover:shadow-lg">
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Page header */}
        <div className="bg-[#E8F1F9] py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-[#002D62]">Frequently Asked Questions</h1>
              <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
                Find answers to common questions about our insurance services, policies, and coverage options.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ content section */}
        <div className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row">
              {/* Sidebar for desktop */}
              <div className={`lg:block ${sidebarOpen ? 'block' : 'hidden'} lg:w-1/4 mb-6 lg:mb-0 lg:pr-8`}>
                <div className="sticky top-24 bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-[#002D62] mb-4">FAQ Categories</h3>
                  
                  {/* Search box */}
                  <div className="mb-6 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search FAQs..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  
                  {/* Category list */}
                  <ul className="space-y-2">
                    {categories.map((category) => (
                      <li key={category.id}>
                        <button
                          onClick={() => setActiveCategory(category.id)}
                          className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                            activeCategory === category.id
                              ? 'bg-[#0077B6] text-white font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {category.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Call to action */}
                  <div className="mt-8 bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white p-4 rounded-lg">
                    <h4 className="font-bold text-lg mb-2">Need more help?</h4>
                    <p className="text-sm mb-4">Our insurance experts are ready to answer your specific questions.</p>
                    <a 
                      href="/contact"
                      className="block w-full bg-white text-[#002D62] text-center py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Mobile sidebar toggle */}
              <div className="lg:hidden mb-4 flex justify-between items-center bg-white rounded-lg shadow-sm p-4">
                <h3 className="font-bold text-[#002D62]">FAQ Categories</h3>
                <button
                  onClick={toggleSidebar}
                  className="text-[#0077B6] hover:text-[#002D62]"
                >
                  {sidebarOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </div>
              
              {/* FAQ content */}
              <div className="lg:w-3/4">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Category title */}
                  <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <h2 className="text-xl font-bold text-[#002D62]">
                      {categories.find(cat => cat.id === activeCategory)?.name || 'All Questions'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {filteredFAQs.length} {filteredFAQs.length === 1 ? 'question' : 'questions'} found
                    </p>
                  </div>
                  
                  {/* FAQ list */}
                  <div className="divide-y divide-gray-200">
                    {filteredFAQs.length > 0 ? (
                      filteredFAQs.map((faq) => (
                        <div key={faq.id} className="px-6 py-4">
                          <button
                            onClick={() => toggleFAQ(faq.id)}
                            className="flex justify-between items-center w-full text-left"
                          >
                            <h3 className="text-lg font-medium text-[#002D62]">{faq.question}</h3>
                            <div className="ml-4 flex-shrink-0">
                              {expandedFAQs[faq.id] ? (
                                <ChevronDown className="h-5 w-5 text-[#0077B6]" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-[#0077B6]" />
                              )}
                            </div>
                          </button>
                          {expandedFAQs[faq.id] && (
                            <div className="mt-3 text-gray-700 bg-gray-50 p-4 rounded-md">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <p className="text-lg text-gray-600">No FAQs found matching your search.</p>
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setActiveCategory('all');
                          }}
                          className="mt-4 text-[#0077B6] font-medium hover:underline"
                        >
                          Clear search and show all FAQs
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Additional help section */}
                <div className="mt-8 bg-[#E8F1F9] rounded-lg p-6">
                  <div className="md:flex md:items-center md:justify-between">
                    <div className="md:w-2/3">
                      <h3 className="text-xl font-bold text-[#002D62]">Still have questions?</h3>
                      <p className="mt-2 text-gray-700">
                        Our insurance specialists are ready to assist you with personalized answers to your specific insurance questions.
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0 md:w-1/3 md:text-right">
                      <a
                        href="/contact"
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#0077B6] hover:bg-[#002D62] transition-colors"
                      >
                        Contact Us
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Related resources section */}
        <div className="bg-gray-50 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#002D62] text-center mb-8">Related Resources</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-40 bg-[#002D62] flex items-center justify-center">
                  <Shield className="h-16 w-16 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#002D62] mb-2">Insurance Glossary</h3>
                  <p className="text-gray-600 mb-4">Understand common insurance terms and concepts with our comprehensive glossary.</p>
                  <a href="/resources/glossary" className="text-[#0077B6] font-medium flex items-center hover:underline">
                    View Glossary
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </a>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-40 bg-[#0077B6] flex items-center justify-center">
                  <Award className="h-16 w-16 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#002D62] mb-2">Insurance Guide for Seniors</h3>
                  <p className="text-gray-600 mb-4">Special considerations and options for senior citizens seeking optimal coverage.</p>
                  <a href="/resources/senior-guide" className="text-[#0077B6] font-medium flex items-center hover:underline">
                    Read Guide
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </a>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-40 bg-[#002D62] flex items-center justify-center">
                  <Users className="h-16 w-16 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#002D62] mb-2">Claims Process Overview</h3>
                  <p className="text-gray-600 mb-4">Step-by-step guide to filing and processing insurance claims with Optimal Actuarion.</p>
                  <a href="/resources/claims-guide" className="text-[#0077B6] font-medium flex items-center hover:underline">
                    View Process
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Call to action */}
        <div className="bg-[#E8F1F9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="md:flex md:items-center md:space-x-8">
              <div className="md:w-2/3 mb-6 md:mb-0">
                <h2 className="text-3xl font-bold text-[#002D62]">Ready to secure your future?</h2>
                <p className="mt-3 text-lg text-gray-700">
                  Get in touch with our experts today for a personalized insurance solution that fits your needs and budget.
                </p>
              </div>
              <div className="md:w-1/3 flex justify-center md:justify-end">
                <a href="/quote" className="w-full md:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-white bg-[#0077B6] hover:bg-[#002D62]">
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#002D62] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Optimal Actuarion LLC</h3>
              <p className="mb-4">Insurance solutions for everyone, with a special focus on senior citizens and underserved communities.</p>
              <div className="flex flex-col space-y-2">
                <a href="/about" className="hover:text-gray-300">About Us</a>
                <a href="/services" className="hover:text-gray-300">Services</a>
                <a href="/testimonials" className="hover:text-gray-300">Testimonials</a>
                <a href="/faqs" className="hover:text-gray-300">FAQs</a>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="flex flex-col space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Phone className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>972-891-8635</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Mail className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>contact@optimalactuarion.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Clock className="h-5 w-5 text-[#0077B6]" />
                  </div>
                  <div className="ml-3">
                    <p>Mon-Fri: 9am-6pm CT</p>
                    <p>Sat: 10am-2pm CT</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Newsletter</h3>
              <p className="mb-4">Subscribe to our newsletter for the latest updates on insurance trends and exclusive offers.</p>
              <form className="mt-4">
                <div className="flex">
                  <input 
                    type="email" 
                    placeholder="Your email address" 
                    className="px-4 py-2 rounded-l-md w-full focus:outline-none text-gray-900"
                  />
                  <button 
                    type="submit" 
                    className="bg-[#0077B6] px-4 py-2 rounded-r-md hover:bg-[#00A8E8] transition-colors"
                  >
                    Subscribe
                  </button>
                </div>
              </form>
              <div className="mt-4 flex space-x-4">
                <a href="https://facebook.com" className="hover:text-[#00A8E8]">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://twitter.com" className="hover:text-[#00A8E8]">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="https://linkedin.com" className="hover:text-[#00A8E8]">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between">
            <div className="mb-4 md:mb-0">
              <p>&copy; {new Date().getFullYear()} Optimal Actuarion LLC. All rights reserved.</p>
            </div>
            <div className="flex space-x-6">
              <a href="/privacy" className="text-sm hover:text-gray-300">Privacy Policy</a>
              <a href="/terms" className="text-sm hover:text-gray-300">Terms of Service</a>
              <a href="/accessibility" className="text-sm hover:text-gray-300">Accessibility</a>
              <a href="/sitemap" className="text-sm hover:text-gray-300">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}